import { AlertTriangle, CheckCircle2, AlertCircle, Info, X } from "lucide-react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { useToast } from "@/components/ui/use-toast"
import { cn } from "@/lib/utils"

// Toaster for the candidate video interview pages. It reads the same useToast()
// store as the global Toaster, so existing toast() calls need no changes; it only
// swaps the look for the interview's warm-paper palette (jobsvox-tokens.css).
// The interview flow is light-only, hence force-light on the viewport.
// No copy button: candidates read these, they don't paste them into bug reports.

type Variant = "default" | "destructive" | "success" | "warning"

const variantStyles: Record<Variant, { card: string; icon: string; title: string; description: string; Icon: typeof Info }> = {
  destructive: {
    card: "bg-danger-bg border-danger-line",
    icon: "bg-card text-danger",
    title: "text-danger",
    description: "text-danger/85",
    Icon: AlertTriangle,
  },
  success: {
    card: "bg-card border-border",
    icon: "bg-good-bg text-good",
    title: "text-foreground",
    description: "text-muted-foreground",
    Icon: CheckCircle2,
  },
  warning: {
    card: "bg-card border-border",
    icon: "bg-mid-bg text-mid",
    title: "text-foreground",
    description: "text-muted-foreground",
    Icon: AlertCircle,
  },
  default: {
    card: "bg-card border-border",
    icon: "bg-[#f3ecf8] text-[#8437BB]",
    title: "text-foreground",
    description: "text-muted-foreground",
    Icon: Info,
  },
}

export function VideoCallToaster() {
  const { toasts } = useToast()

  return (
    <ToastPrimitives.Provider>
      {toasts.map(({ id, title, description, action, variant, className, ...props }) => {
        const styles = variantStyles[(variant as Variant) ?? "default"] ?? variantStyles.default
        const { Icon } = styles

        return (
          <ToastPrimitives.Root
            key={id}
            {...props}
            className={cn(
              "group pointer-events-auto relative grid w-full grid-cols-[auto_1fr] items-start gap-3 overflow-hidden rounded-2xl border p-3.5 pr-10 font-sans shadow-[0_1px_2px_rgba(27,27,24,.04),0_8px_24px_-6px_rgba(27,27,24,.12)] transition-all",
              "data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none",
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-bottom-full",
              styles.card,
              className
            )}
          >
            <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]", styles.icon)}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="grid min-w-0 gap-0.5">
              {title && (
                <ToastPrimitives.Title className={cn("text-sm font-semibold leading-snug", styles.title)}>
                  {title}
                </ToastPrimitives.Title>
              )}
              {description && (
                <ToastPrimitives.Description className={cn("text-[13px] leading-normal", styles.description)}>
                  {description}
                </ToastPrimitives.Description>
              )}
              {action}
            </div>
            <ToastPrimitives.Close
              aria-label="Close"
              className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-border hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8437BB]"
            >
              <X className="h-3.5 w-3.5" />
            </ToastPrimitives.Close>
          </ToastPrimitives.Root>
        )
      })}
      <ToastPrimitives.Viewport className="force-light fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col gap-2.5 p-4 md:max-w-[420px]" />
    </ToastPrimitives.Provider>
  )
}
